import {
  Component,
  ElementRef,
  computed,
  inject,
  input,
  output,
  signal,
  viewChild,
} from '@angular/core';
import { AuthService } from '../auth/auth.service';
import { VersionService } from '../services/version.service';
import { UiAlert } from '../../shared/ui/alert';
import { UiButton } from '../../shared/ui/button.directive';
import { UiModal } from '../../shared/ui/modal';
import { iniciales, nombreUsuario, rolUsuario } from './usuario-display';

/** Tamaño interno del lienzo de la firma (proporción 2.5:1, se guarda como PNG). */
const LIENZO_ANCHO = 600;
const LIENZO_ALTO = 240;

/**
 * Modal de perfil del usuario: muestra la información sincronizada desde
 * SIGERH y permite guardar una firma digitalizada (dibujada o subida como
 * imagen) que queda asociada al usuario para futuros usos en informes.
 */
@Component({
  selector: 'app-perfil-modal',
  imports: [UiAlert, UiButton, UiModal],
  templateUrl: './perfil-modal.html',
})
export class PerfilModal {
  readonly abierto = input.required<boolean>();
  readonly cerrar = output<void>();

  private readonly auth = inject(AuthService);
  private readonly versiones = inject(VersionService);

  protected readonly usuario = this.auth.usuario;
  protected readonly nombre = computed(() => nombreUsuario(this.usuario()));
  protected readonly iniciales = computed(() => iniciales(this.usuario()));
  protected readonly rol = computed(() => rolUsuario(this.usuario()));

  /** Build de la aplicación web, para reportar problemas con datos exactos. */
  protected readonly frontend = this.versiones.frontend;

  protected commitCorto(commit: string | undefined): string {
    return this.versiones.commitCorto(commit);
  }

  protected fechaLegible(iso: string | undefined): string {
    return this.versiones.fechaLegible(iso);
  }

  /** true mientras se dibuja/sube una firma nueva. */
  protected readonly editando = signal(false);
  /** true cuando el lienzo tiene algo que guardar. */
  protected readonly hayTrazo = signal(false);
  protected readonly guardando = signal(false);
  protected readonly error = signal<string | null>(null);

  private readonly lienzo = viewChild<ElementRef<HTMLCanvasElement>>('lienzo');
  private dibujando = false;

  protected cerrarModal(): void {
    this.cancelarEdicion();
    this.cerrar.emit();
  }

  // ── Edición de la firma ────────────────────────────────────────────────

  protected iniciarEdicion(): void {
    this.error.set(null);
    this.hayTrazo.set(false);
    this.editando.set(true);
  }

  protected cancelarEdicion(): void {
    this.editando.set(false);
    this.hayTrazo.set(false);
    this.dibujando = false;
    this.error.set(null);
  }

  protected limpiarLienzo(): void {
    const canvas = this.lienzo()?.nativeElement;
    canvas?.getContext('2d')?.clearRect(0, 0, LIENZO_ANCHO, LIENZO_ALTO);
    this.hayTrazo.set(false);
  }

  // Dibujo a mano alzada sobre el lienzo (mouse, touch o lápiz).

  protected empezarTrazo(evento: PointerEvent): void {
    const ctx = this.contexto();
    if (!ctx) {
      return;
    }
    this.dibujando = true;
    const { x, y } = this.coordenadas(evento);
    ctx.beginPath();
    ctx.moveTo(x, y);
    // Un punto visible aunque el usuario solo haga clic.
    ctx.lineTo(x + 0.1, y + 0.1);
    ctx.stroke();
    this.hayTrazo.set(true);
  }

  protected continuarTrazo(evento: PointerEvent): void {
    if (!this.dibujando) {
      return;
    }
    const ctx = this.contexto();
    if (!ctx) {
      return;
    }
    const { x, y } = this.coordenadas(evento);
    ctx.lineTo(x, y);
    ctx.stroke();
  }

  protected terminarTrazo(): void {
    this.dibujando = false;
  }

  /** Carga una imagen (PNG/JPG/WEBP) y la encaja en el lienzo. */
  protected subirImagen(evento: Event): void {
    const archivo = (evento.target as HTMLInputElement).files?.[0];
    (evento.target as HTMLInputElement).value = '';
    if (!archivo) {
      return;
    }
    if (!/^image\/(png|jpe?g|webp)$/.test(archivo.type)) {
      this.error.set('La firma debe ser una imagen PNG, JPG o WEBP.');
      return;
    }
    const lector = new FileReader();
    lector.onload = () => {
      const imagen = new Image();
      imagen.onload = () => {
        const ctx = this.contexto();
        if (!ctx) {
          return;
        }
        ctx.clearRect(0, 0, LIENZO_ANCHO, LIENZO_ALTO);
        // Encaja la imagen dentro del lienzo conservando la proporción.
        const escala = Math.min(LIENZO_ANCHO / imagen.width, LIENZO_ALTO / imagen.height, 1);
        const ancho = imagen.width * escala;
        const alto = imagen.height * escala;
        ctx.drawImage(imagen, (LIENZO_ANCHO - ancho) / 2, (LIENZO_ALTO - alto) / 2, ancho, alto);
        this.error.set(null);
        this.hayTrazo.set(true);
      };
      imagen.onerror = () => this.error.set('No se pudo leer la imagen seleccionada.');
      imagen.src = lector.result as string;
    };
    lector.readAsDataURL(archivo);
  }

  protected guardarFirma(): void {
    const canvas = this.lienzo()?.nativeElement;
    if (!canvas || !this.hayTrazo()) {
      return;
    }
    this.guardando.set(true);
    this.error.set(null);
    this.auth.guardarFirma(canvas.toDataURL('image/png')).subscribe({
      next: () => {
        this.guardando.set(false);
        this.cancelarEdicion();
      },
      error: () => {
        this.guardando.set(false);
        this.error.set('No se pudo guardar la firma. Intente de nuevo.');
      },
    });
  }

  protected quitarFirma(): void {
    this.guardando.set(true);
    this.error.set(null);
    this.auth.eliminarFirma().subscribe({
      next: () => this.guardando.set(false),
      error: () => {
        this.guardando.set(false);
        this.error.set('No se pudo quitar la firma. Intente de nuevo.');
      },
    });
  }

  // ── Utilidades del lienzo ─────────────────────────────────────────────

  private contexto(): CanvasRenderingContext2D | null {
    const canvas = this.lienzo()?.nativeElement;
    const ctx = canvas?.getContext('2d') ?? null;
    if (ctx) {
      ctx.lineWidth = 2.5;
      ctx.lineCap = 'round';
      ctx.lineJoin = 'round';
      ctx.strokeStyle = '#1f2937';
    }
    return ctx;
  }

  /** Traduce el puntero a coordenadas internas del lienzo (que es responsive). */
  private coordenadas(evento: PointerEvent): { x: number; y: number } {
    const canvas = this.lienzo()!.nativeElement;
    const rect = canvas.getBoundingClientRect();
    return {
      x: ((evento.clientX - rect.left) / rect.width) * LIENZO_ANCHO,
      y: ((evento.clientY - rect.top) / rect.height) * LIENZO_ALTO,
    };
  }
}
